import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Theme context — light / dark / system.
 *
 * The whole app is token-based (see index.css), so switching modes is
 * just toggling `.dark` on <html>; every `bg-ink-*` / `text-ink-*` /
 * `bg-paper` utility reads the flipped CSS variables automatically.
 *
 * Persisted in localStorage so the choice survives refresh. "system"
 * (the default) tracks the OS `prefers-color-scheme` live. To avoid a
 * flash of the wrong theme on first paint, index.html runs a tiny
 * blocking script that applies the class before React mounts — this
 * provider keeps it in sync afterwards.
 */

const STORAGE_KEY = "lingua.theme";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

type ThemeValue = {
  /** The user's choice — what the selector shows. */
  theme: ThemePreference;
  setTheme: (next: ThemePreference) => void;
  /** What's actually applied right now (system resolved to light/dark). */
  resolved: ResolvedTheme;
};

const ThemeContext = createContext<ThemeValue | null>(null);

function isThemePreference(v: string): v is ThemePreference {
  return v === "light" || v === "dark" || v === "system";
}

/** Read the stored preference once, synchronously. Falls back to
 *  "system" for empty / corrupt / SSR values. Exported so the
 *  no-flash inline script's logic and this module agree on the key. */
export function readStoredTheme(): ThemePreference {
  if (typeof window === "undefined") return "system";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && isThemePreference(raw)) return raw;
  } catch {
    // SecurityError in restricted contexts (Safari private mode etc.).
  }
  return "system";
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolve(pref: ThemePreference): ResolvedTheme {
  if (pref === "system") return systemPrefersDark() ? "dark" : "light";
  return pref;
}

/** Apply the resolved theme to <html>. Single place that touches the DOM
 *  class so the inline script and the provider can both call it. */
function applyTheme(resolved: ResolvedTheme) {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolved === "dark");
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(() =>
    readStoredTheme(),
  );
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    resolve(readStoredTheme()),
  );

  const setTheme = useCallback((next: ThemePreference) => {
    setThemeState(next);
    const nextResolved = resolve(next);
    setResolved(nextResolved);
    applyTheme(nextResolved);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // In-memory state still works for the session; just won't persist.
    }
  }, []);

  // Keep the DOM class in sync if `theme` changes by any path, and —
  // when on "system" — follow live OS changes (e.g. the user flips
  // their laptop to night mode while the tab is open).
  useEffect(() => {
    applyTheme(resolve(theme));
    if (theme !== "system" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const next = systemPrefersDark() ? "dark" : "light";
      setResolved(next);
      applyTheme(next);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const value = useMemo(
    () => ({ theme, setTheme, resolved }),
    [theme, setTheme, resolved],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be called inside <ThemeProvider>");
  }
  return ctx;
}
